// Idle logout + 2-minute warning (security policy, Phase 3).
//
// - Only REAL activity counts: mouse movement inside the app, pointer down, key
//   press, mouse wheel, touch, and input into forms (also autofill, paste from the
//   menu, phone keyboard suggestions, pickers). Only events made by the person count
//   (isTrusted) — events fired by code don't. API polling, automatic refreshes,
//   sockets, notifications and timers never reset the clock.
//   Mouse movement is checked at most every 5 s and only when the pointer really moved.
// - Activity is shared by every tab of this app (localStorage), so working in
//   one tab keeps the others signed in, and a logout in one tab ends them all.
// - At most once a minute the activity is reported to the server
//   (POST /auth/activity); the server refuses the token once the idle limit
//   has passed, even if this page is closed or tampered with.
// - Before showing the warning the server is asked when it last saw activity,
//   so work done inside an embedded app (kanban / HR iframes report their own
//   activity) or another tab also counts.
// - The 8-hour maximum session is announced 2 minutes before it ends.
//
// Rules (idle minutes, warning seconds, 8 hours) come from the server: GET /auth/session.

const KEY_ACTIVITY = "bu.session.lastActivity";
const KEY_REPORTED = "bu.session.lastReported";
const REPORT_EVERY_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "input", "change"];
const MOVE_EVENT = "mousemove";
const MOVE_EVERY_MS = 5000;
const MOVE_MIN_PX = 3; // ignore tiny jitter and "moves" fired without the pointer moving
const DEFAULT_POLICY = { idleMinutes: 30, sensitiveIdleMinutes: 10, warningSeconds: 120 };

// Only events the person made count; events dispatched by code (isTrusted false) never do.
const realOnly = (fn) => (e) => {
  if (e && e.isTrusted === false) return;
  fn(e);
};

// Mouse movement → activity, throttled (mousemove fires many times a second).
const moveFilter = (onActivity) => {
  let lastAt = 0;
  let lastX = null;
  let lastY = null;
  return (e) => {
    if (e && e.isTrusted === false) return;
    const now = Date.now();
    if (now - lastAt < MOVE_EVERY_MS) return;
    const moved = lastX === null || Math.abs(e.screenX - lastX) + Math.abs(e.screenY - lastY) >= MOVE_MIN_PX;
    lastX = e.screenX;
    lastY = e.screenY;
    if (!moved) return;
    lastAt = now;
    onActivity();
  };
};

const readNum = (key) => {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch (e) {
    return 0;
  }
};
const writeNum = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch (e) {
    /* storage blocked: this tab still tracks itself */
  }
};

// Some apps store the token JSON-encoded ("\"eyJ...\"").
export const cleanToken = (token) => String(token || "").replace(/^"|"$/g, "");

const request = async (baseUrl, path, { method = "GET", token, companyId, body } = {}) => {
  const headers = { Authorization: `Bearer ${cleanToken(token)}` };
  if (companyId) headers["company-id"] = companyId;
  if (body) headers["Content-Type"] = "application/json";
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    /* empty body */
  }
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data && data.data ? data.data : data;
};

/**
 * Embedded apps (kanban / HR inside the admin app): only report real activity
 * to the server, the parent app owns the timer and the dialog.
 */
export const startActivityReporter = ({ baseUrl, getToken, getCompanyId }) => {
  const onActivity = realOnly(() => {
    const now = Date.now();
    if (now - readNum(KEY_REPORTED) < REPORT_EVERY_MS) return;
    const token = getToken();
    if (!token) return;
    writeNum(KEY_REPORTED, now);
    request(baseUrl, "/auth/activity", { method: "POST", token, companyId: getCompanyId && getCompanyId(), body: {} }).catch(() => {});
  });
  const onMove = moveFilter(onActivity);
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { capture: true, passive: true }));
  window.addEventListener(MOVE_EVENT, onMove, { capture: true, passive: true });
  return () => {
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity, { capture: true }));
    window.removeEventListener(MOVE_EVENT, onMove, { capture: true });
  };
};

/**
 * Full tracker for the top-level app.
 * onState({ phase: 'active'|'idle-warning'|'expiry-warning', secondsLeft })
 * onLogout(reason: 'idle'|'expired'|'server'|'other-tab', message?)
 * Returns { stay(), dismissExpiry(), stop() }.
 */
export const startIdleTracker = ({ baseUrl, getToken, getCompanyId, isSensitive, onState, onLogout }) => {
  let policy = { ...DEFAULT_POLICY };
  let expiresAt = null; // absolute end, in this browser's clock
  let skew = 0; // server clock - browser clock
  let phase = "active";
  let checkingServer = false;
  let serverCheckedAt = 0; // when the server was last asked about activity
  let expiryDismissed = false;
  let ended = false;
  // The login these rules / end time belong to. Logging in again in another tab gives every
  // tab a NEW session: the old end time must then be forgotten, or this tab would end the
  // new session (and log every tab out) when the OLD 8 hours are over.
  let sessionToken = null;

  const companyId = () => (getCompanyId ? getCompanyId() : null);

  const applySession = (s) => {
    if (!s) return;
    policy = {
      idleMinutes: s.idleMinutes === undefined ? policy.idleMinutes : s.idleMinutes,
      sensitiveIdleMinutes: s.sensitiveIdleMinutes === undefined ? policy.sensitiveIdleMinutes : s.sensitiveIdleMinutes,
      warningSeconds: s.warningSeconds || policy.warningSeconds,
    };
    if (s.serverTime) skew = new Date(s.serverTime).getTime() - Date.now();
    if (s.expiresAt) expiresAt = new Date(s.expiresAt).getTime() - skew;
    if (s.lastActivityAt) {
      const serverLast = new Date(s.lastActivityAt).getTime() - skew;
      if (serverLast > readNum(KEY_ACTIVITY)) writeNum(KEY_ACTIVITY, serverLast);
    }
  };

  const end = (reason, message) => {
    if (ended) return;
    ended = true;
    stop();
    onLogout(reason, message);
  };

  const handleServerError = (err) => {
    // The server already ended this session (logged out elsewhere, idle, 8 hours, disabled).
    if (err && err.status === 401) end("server", err.data && err.data.message);
  };

  const report = (force) => {
    const now = Date.now();
    if (!force && now - readNum(KEY_REPORTED) < REPORT_EVERY_MS) return;
    const token = getToken();
    if (!token) return;
    writeNum(KEY_REPORTED, now);
    request(baseUrl, "/auth/activity", { method: "POST", token, companyId: companyId(), body: {} })
      .then((s) => {
        if (getToken() === token) applySession(s);
      })
      .catch(handleServerError);
  };

  const markActivity = (force) => {
    if (ended) return;
    writeNum(KEY_ACTIVITY, Date.now());
    report(force);
    if (phase === "idle-warning") setPhase("active", 0);
  };

  const onActivity = realOnly(() => {
    // While the warning is open only its buttons count (a stray click on the page behind must not hide it).
    if (phase === "idle-warning") return;
    markActivity(false);
  });

  const setPhase = (next, secondsLeft) => {
    phase = next;
    onState({ phase: next, secondsLeft });
  };

  const checkServer = () => {
    const token = getToken();
    if (!token || checkingServer) return;
    checkingServer = true;
    request(baseUrl, "/auth/session", { token, companyId: companyId() })
      .then((s) => {
        if (getToken() !== token) return; // logged in again meanwhile: these rules are for the old session
        sessionToken = token;
        applySession(s);
      })
      .catch(handleServerError)
      .finally(() => {
        checkingServer = false;
        serverCheckedAt = Date.now();
        tick();
      });
  };

  const tick = () => {
    if (ended) return;
    const token = getToken();
    if (!token) return end("other-tab");
    if (sessionToken && token !== sessionToken) {
      // another tab logged in again: new session, new end time — ask the server
      sessionToken = token;
      expiresAt = null;
      expiryDismissed = false;
      serverCheckedAt = 0;
      if (phase !== "active") setPhase("active", 0);
      return checkServer();
    }
    const now = Date.now();
    const warnMs = policy.warningSeconds * 1000;

    // 8-hour maximum: cannot be extended, only announced. The server confirms first.
    if (expiresAt && now >= expiresAt) {
      if (serverCheckedAt < expiresAt) return checkServer();
      return end("expired");
    }

    const limit = isSensitive && isSensitive() ? policy.sensitiveIdleMinutes : policy.idleMinutes;
    if (limit) {
      const last = readNum(KEY_ACTIVITY) || now;
      const idleEndsAt = last + limit * 60000;
      if (now >= idleEndsAt - warnMs) {
        // Ask the server once per warning whether activity happened elsewhere (iframe, other tab) first.
        if (serverCheckedAt < idleEndsAt - warnMs) return checkServer();
        if (now >= idleEndsAt) return end("idle");
        return setPhase("idle-warning", Math.ceil((idleEndsAt - now) / 1000));
      }
    }
    if (expiresAt && !expiryDismissed && now >= expiresAt - warnMs) {
      return setPhase("expiry-warning", Math.ceil((expiresAt - now) / 1000));
    }
    if (phase !== "active") setPhase("active", 0);
  };

  const onStorage = (e) => {
    // Another tab logged out (localStorage.clear() reports key null).
    if ((e.key === null || e.key === "access_token") && !getToken()) end("other-tab");
    else if (e.key === KEY_ACTIVITY) tick();
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") tick();
  };

  const onMove = moveFilter(onActivity);
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { capture: true, passive: true }));
  window.addEventListener(MOVE_EVENT, onMove, { capture: true, passive: true });
  window.addEventListener("storage", onStorage);
  document.addEventListener("visibilitychange", onVisible);
  const timer = window.setInterval(tick, 1000);

  function stop() {
    window.clearInterval(timer);
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity, { capture: true }));
    window.removeEventListener(MOVE_EVENT, onMove, { capture: true });
    window.removeEventListener("storage", onStorage);
    document.removeEventListener("visibilitychange", onVisible);
  }

  // Opening / reloading the app is activity; then load the rules and the session end time.
  markActivity(true);
  checkServer();

  return {
    stay: () => markActivity(true),
    dismissExpiry: () => {
      expiryDismissed = true;
      setPhase("active", 0);
    },
    stop: () => {
      ended = true;
      stop();
    },
  };
};

export const LOGOUT_MESSAGES = {
  idle: "You were logged out due to inactivity. Please log in again.",
  expired: "Your session reached its maximum length (8 hours). Please log in again.",
};
